import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { syncTigerIosChromeColors } from '../tiger/tigerBridgeChromeColor';

/**
 * ThemeProvider del LoginPortal — misma clave y contrato que el shell WMS (`wms-theme`).
 * Sincroniza `data-theme` y `data-bs-theme` en documentElement para tokens CSS y Bootstrap Icons.
 */
const ThemeContext = createContext(null);

const THEME_STORAGE_KEY = 'wms-theme';
const LIGHT_THEME = 'light';
const DARK_THEME = 'dark';

const isValidTheme = (value) => value === LIGHT_THEME || value === DARK_THEME;

const getInitialTheme = () => {
    if (typeof window === 'undefined') return LIGHT_THEME;

    try {
        const savedTheme = String(localStorage.getItem(THEME_STORAGE_KEY) || '').trim();
        if (isValidTheme(savedTheme)) return savedTheme;
    } catch {
        /* ignore storage errors */
    }

    return LIGHT_THEME;
};

const syncThemeOnDom = (theme) => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    root.setAttribute('data-theme', theme);
    root.setAttribute('data-bs-theme', theme);
};

export const ThemeProvider = ({ children }) => {
    const [theme, setTheme] = useState(getInitialTheme);

    useLayoutEffect(() => {
        syncThemeOnDom(theme);
        syncTigerIosChromeColors({ preset: 'portal' });
    }, [theme]);

    useEffect(() => {
        try {
            localStorage.setItem(THEME_STORAGE_KEY, theme);
        } catch {
            /* ignore storage errors */
        }
    }, [theme]);

    const setLightTheme = useCallback(() => setTheme(LIGHT_THEME), []);
    const setDarkTheme = useCallback(() => setTheme(DARK_THEME), []);
    const toggleTheme = useCallback(() => {
        setTheme((prev) => (prev === DARK_THEME ? LIGHT_THEME : DARK_THEME));
    }, []);

    const value = useMemo(
        () => ({
            theme,
            isDarkMode: theme === DARK_THEME,
            setTheme,
            setLightTheme,
            setDarkTheme,
            toggleTheme,
        }),
        [theme, setLightTheme, setDarkTheme, toggleTheme],
    );

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within ThemeProvider');
    }
    return context;
};
